<template>
  <div class="help-center">
    <div class="z-10 relative">
      <div
        class="banner h-[266px] relative mb-[32px]"
        :style="{
          backgroundImage: `url(${transformImage(configData?.cover_url)})`,
        }"
      >
        <div class="text-center pt-[70px]">
          <h1 class="text-[24px] my-0 font-bold text-[var(--help-text-color)]">
            {{ configData?.title }}
          </h1>
          <div class="mt-[20px]">
            <Search
              ref="searchRef"
              v-model:model-value="keyword"
              type="main"
              size="large"
              class="w-[690px] mx-auto"
              :hotLength="5"
              :showRecommend="true"
              :placeholder="$t('qing-shu-ru-wen-ti-guan-jian-ci-ru-qi-ye-ban-quan-yi')"
              :area="SEARCH_RECORD_UNIQUE.HELP_CENTER"
              @search="handleOnSearch"
            >
            </Search>
          </div>
          <HotKeys
            class="w-[460px] mt-[20px]"
            :area="SEARCH_RECORD_UNIQUE.HELP_CENTER"
            @select="selectTag"
          />
        </div>
      </div>
      <!-- 热门推荐 -->
      <HotRecommend v-if="!isSearch" />

      <div class="w-[1000px] mx-auto mt-[32px] pb-[80px]">
        <div v-if="!isSearch" class="help-category-cards">
          <CardItem v-for="item in helpCategory" :key="item.id" :item="item" />
        </div>
        <div v-else class="result">
          <a-spin :spinning="loadingResult" class="block mx-auto" />
          <div v-if="!loadingResult">
            <div v-if="resultData?.length">
              <div class="text-font-40 mb-[32px] text-[14px]">
                {{ $t('gong-zhao-dao') }}
                <span class="text-[var(--purple-6)] font-bold">
                  {{ pagination.total }}
                </span>
                {{ $t('tiao-shu-ju') }}
              </div>
              <ResultItem
                v-for="(item, index) in resultData"
                :key="item.id"
                :item="item"
                :keywords="keyword"
                :divider="index !== resultData.length - 1"
              />
              <div class="text-center mt-[56px]">
                <a-pagination
                  v-model:current="pagination.current"
                  :total="pagination.total"
                  :page-size="pagination.pageSize"
                  @change="onChange"
                />
              </div>
            </div>
            <Empty
              v-if="!pagination.total"
              :list="emptyList?.data"
              @select="selectTag"
              @refresh="emptyRefresh"
              @openCustomerService="handleOpenCustomerService"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" name="HelpCenter">
// 页面主体相关组件引入
import ResultItem from './components/main/resultItem.vue';
import CardItem from './components/main/mainItem.vue';

// 热门推荐组件引入
import HotRecommend from './components/hot-recommend/index.vue';

// 搜索相关组件引入
import { useWatchPageSeoData } from '~/hooks/useSeo';
import Empty from '@/features/searchInput/empty.vue';
import Search from '@/features/searchInput/index.vue';
import HotKeys from '@/features/searchInput/hotKeys.vue';

// 底部相关组件引入
import {
  getPromotionCenterPageCard,
  getHelpCategory,
  getSearchCategoryResult,
  type IHelpSearch,
} from '~/api/index';
import { CONFIG_TYPE, SEARCH_HOT_WORD, SEARCH_RECORD_UNIQUE } from '~/constants';
import { PAGE_CARD_TYPE } from '~/api/interface/publicize';
import { getSiteHotWord } from '~/api/module/search';
import { SearchModelType } from '~/api/interface/search';
import { transformImage } from '~/utils/';
import { ClickType } from '~/features/searchInput/constant';

// 先声明响应式变量
const keyword = ref('');
const searchRef = ref();
const isSearch = ref(false);
const loadingResult = ref(true);
const resultData = ref<IHelpSearch[]>([]);
const pagination = ref<{
  page: number;
  pageSize: number;
  total: number;
  current: number;
}>({
  page: 1,
  pageSize: 10,
  total: 0,
  current: 1,
});

const { data: emptyList, refresh: emptyRefresh } = useAsyncData(() =>
  getSiteHotWord(SEARCH_HOT_WORD.HELP, 6),
);

const resetPaginationAndResultData = () => {
  pagination.value.current = 1;
  resultData.value = [];
  isSearch.value = false;
};

const selectTag = (val: string) => {
  if (val === keyword.value) {
    return;
  }

  keyword.value = val;
  resetPaginationAndResultData();

  nextTick(() => {
    searchRef.value?.search(val, ClickType['热搜词']);
  });
};

const onChange = (page: number, pageSize: number) => {
  pagination.value.current = page;
  fetchResult();
};

const fetchResult = async (val?: string) => {
  try {
    loadingResult.value = true;

    const res: any = await getSearchCategoryResult({
      keyword: val || keyword.value,
      page: pagination.value.current,
      page_size: pagination.value.pageSize,
    });

    if (res.data) {
      resultData.value = res.data || [];
      pagination.value.total = res.count || 0;
    } else {
      resultData.value = [];
      pagination.value.total = 0;
    }

    loadingResult.value = false;

    trackEvent(TRACK_EVENT.CLICK_SITE_SEARCH_RESULT_LOAD, {
      keywords: val || keyword.value,
      count: res.data?.count || 0,
    });
  } catch (error) {
    console.error('获取搜索结果出现错误:', error);
    resultData.value = [];
    pagination.value.total = 0;
    loadingResult.value = false;
  }
};

const handleOnSearch = (val: string) => {
  keyword.value = val;

  if (!val) {
    isSearch.value = false;
    resetPaginationAndResultData();
    return;
  } else {
    isSearch.value = true;
    pagination.value.current = 1;
    fetchResult(val);
  }
};

const { data } = useAsyncData(() => getHelpCategory({ model_type: SearchModelType.HELP_CENTER }));

const { data: pageInfo } = useAsyncData(() =>
  getPromotionCenterPageCard({
    type: PAGE_CARD_TYPE.HEL_CENTER,
  }),
);

const helpCategory = computed(() => data.value?.data || []);

const configData = computed(() =>
  pageInfo.value?.data?.find(item => item.type === CONFIG_TYPE.HELP_CENTER),
);

const handleOpenCustomerService = () => {
  trackEvent(TRACK_EVENT.CLICK_SITE_SEARCH, {
    source: 'help-center',
    keywords: keyword.value,
    click_type: ClickType['联系客服'],
  });
};

useWatchPageSeoData(configData);
</script>

<style lang="scss" scoped>
.help-center {
  @apply relative;
  .help-category-cards {
    @apply grid  mx-auto w-[1000px];
    grid-template-columns: repeat(2, minmax(16px, 1fr));
    row-gap: 20px;
    column-gap: 20px;
    justify-items: center;
    align-items: center;
  }
}
</style>
